import React, { useState, useEffect, useRef } from 'react';
import { Contact, CustomField, Company } from '../types';
import { contactsAPI, customFieldsAPI, companiesAPI } from '../services/api';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { FormField, FormSelect, FormTextarea } from './ui/FormField';
import TagInput from './TagInput';
import { useIndustryConfig } from '../contexts/IndustryConfigContext';
import { normalizePhone } from '../utils/normalizePhone';

// Mortgage 1003 select options
const MARITAL_STATUS_OPTIONS = ['Married', 'Separated', 'Unmarried'];
const CITIZENSHIP_OPTIONS = ['US Citizen', 'Permanent Resident', 'Non-Permanent Resident'];
const INCOME_TYPE_OPTIONS = ['Base', 'Overtime', 'Bonus', 'Commission', 'Military', 'Other'];
const LOAN_PURPOSE_OPTIONS = ['Purchase', 'Refinance', 'Cash-Out Refi'];
const PROPERTY_TYPE_OPTIONS = ['Single Family', 'Condo', 'Townhouse', 'Multi-Unit', 'Manufactured'];
const PROPERTY_USE_OPTIONS = ['Primary Residence', 'Second Home', 'Investment'];
const CREDIT_SCORE_OPTIONS = ['Excellent 740+', 'Good 700-739', 'Fair 660-699', 'Below 660'];
const PRE_QUAL_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Pre-Qualified', 'Pre-Approved', 'Denied'];

// Real Estate select options
const CLIENT_TYPE_OPTIONS = ['Buyer', 'Seller', 'Both'];
const PRE_APPROVAL_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Pre-Approved', 'Not Needed'];
const PREFERRED_PROPERTY_TYPE_OPTIONS = ['Single Family', 'Condo', 'Townhouse', 'Multi-Family', 'Land', 'Other'];
const MOVE_IN_TIMELINE_OPTIONS = ['ASAP', '1-3 Months', '3-6 Months', '6-12 Months', '12+ Months'];
const CURRENT_HOUSING_OPTIONS = ['Renting', 'Own - Selling', 'Own - Keeping', 'Living with Family', 'Other'];
const REASON_FOR_SELLING_OPTIONS = ['Upsizing', 'Downsizing', 'Relocating', 'Investment', 'Divorce', 'Estate', 'Other'];
const DESIRED_TIMELINE_OPTIONS = ['ASAP', '1-3 Months', '3-6 Months', '6-12 Months', 'Flexible'];

interface ContactFormProps {
  contact?: Contact;
  initialData?: Partial<Contact>;
  onSubmit: (contact: Contact) => void;
  onCancel: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ contact, initialData, onSubmit, onCancel }) => {
  const { isFormSectionEnabled } = useIndustryConfig();
  const showBorrowerProfile = isFormSectionEnabled('contact', 'borrowerProfile');
  const showEmploymentIncome = isFormSectionEnabled('contact', 'employmentIncome');
  const showLoanPreferences = isFormSectionEnabled('contact', 'loanPreferences');
  const showPreQualification = isFormSectionEnabled('contact', 'preQualification');
  const showClientProfile = isFormSectionEnabled('contact', 'clientProfile');
  const showSellerProfile = isFormSectionEnabled('contact', 'sellerProfile');

  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  // Merge contact (edit mode) or initialData (pre-fill mode, e.g. business card scan)
  const prefill = contact || initialData;
  const [formData, setFormData] = useState({
    firstName: prefill?.firstName || '',
    lastName: prefill?.lastName || '',
    email: prefill?.email || '',
    phone: normalizePhone(prefill?.phone || ''),
    company: prefill?.company || '',
    companyId: prefill?.companyId || '',
    referredById: prefill?.referredById || '',
    position: prefill?.position || '',
    tags: prefill?.tags || [],
    notes: prefill?.notes || '',
    address1: prefill?.address1 || '',
    address2: prefill?.address2 || '',
    city: prefill?.city || '',
    addressState: prefill?.addressState || '',
    zip: prefill?.zip || '',
    lastContacted: prefill?.lastContacted || '',
    customFields: prefill?.customFields || {},
    // Mortgage 1003 fields
    dateOfBirth: prefill?.dateOfBirth || '',
    maritalStatus: prefill?.maritalStatus || '',
    dependentsCount: prefill?.dependentsCount ?? '',
    citizenshipStatus: prefill?.citizenshipStatus || '',
    employerName: prefill?.employerName || '',
    employerPhone: prefill?.employerPhone || '',
    yearsInProfession: prefill?.yearsInProfession ?? '',
    monthlyIncome: prefill?.monthlyIncome ?? '',
    incomeType: prefill?.incomeType || '',
    selfEmployed: prefill?.selfEmployed || false,
    loanPurpose: prefill?.loanPurpose || '',
    propertyType: prefill?.propertyType || '',
    propertyUse: prefill?.propertyUse || '',
    estimatedPurchasePrice: prefill?.estimatedPurchasePrice ?? '',
    desiredLoanAmount: prefill?.desiredLoanAmount ?? '',
    estimatedCreditScore: prefill?.estimatedCreditScore || '',
    downPaymentPercent: prefill?.downPaymentPercent ?? '',
    firstTimeHomeBuyer: prefill?.firstTimeHomeBuyer || false,
    isVeteran: prefill?.isVeteran || false,
    preQualStatus: prefill?.preQualStatus || '',
    preQualAmount: prefill?.preQualAmount ?? '',
    preQualExpiration: prefill?.preQualExpiration || '',
    // Real Estate — Client Profile
    clientType: prefill?.clientType || '',
    preApprovalStatus: prefill?.preApprovalStatus || '',
    preApprovalAmount: prefill?.preApprovalAmount ?? '',
    priceRangeMin: prefill?.priceRangeMin ?? '',
    priceRangeMax: prefill?.priceRangeMax ?? '',
    desiredArea: prefill?.desiredArea || '',
    preferredPropertyType: prefill?.preferredPropertyType || '',
    bedroomsNeeded: prefill?.bedroomsNeeded ?? '',
    bathroomsNeeded: prefill?.bathroomsNeeded ?? '',
    moveInTimeline: prefill?.moveInTimeline || '',
    currentHousingSituation: prefill?.currentHousingSituation || '',
    hasAgentAgreement: prefill?.hasAgentAgreement || false,
    // Real Estate — Seller Profile
    rePropertyAddress: prefill?.rePropertyAddress || '',
    estimatedHomeValue: prefill?.estimatedHomeValue ?? '',
    mortgageBalance: prefill?.mortgageBalance ?? '',
    reasonForSelling: prefill?.reasonForSelling || '',
    desiredTimeline: prefill?.desiredTimeline || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState(contact?.Company?.name || prefill?.company || '');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);

  // Referrer typeahead state
  const [referrerSearch, setReferrerSearch] = useState(
    contact?.referredBy ? `${contact.referredBy.firstName} ${contact.referredBy.lastName}` : ''
  );
  const [referrerResults, setReferrerResults] = useState<{ id: string; firstName: string; lastName: string; email?: string; phone?: string }[]>([]);
  const [showReferrerDropdown, setShowReferrerDropdown] = useState(false);
  const [showReferrerQuickAdd, setShowReferrerQuickAdd] = useState(false);
  const [referrerQuickAddData, setReferrerQuickAddData] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [creatingReferrer, setCreatingReferrer] = useState(false);
  const referrerRef = useRef<HTMLDivElement>(null);
  const referrerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchCustomFields();
    fetchCompanies();
  }, []);

  // Close company/referrer dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companyRef.current && !companyRef.current.contains(event.target as Node)) {
        setShowCompanyDropdown(false);
      }
      if (referrerRef.current && !referrerRef.current.contains(event.target as Node)) {
        setShowReferrerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data.customFields);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const selectCompany = (company: Company) => {
    setFormData(prev => ({ ...prev, companyId: company.id, company: company.name }));
    setCompanySearch(company.name);
    setShowCompanyDropdown(false);
  };

  const clearCompany = () => {
    setFormData(prev => ({ ...prev, companyId: '', company: '' }));
    setCompanySearch('');
  };

  const handleCreateCompany = async () => {
    if (!companySearch.trim()) return;
    setCreatingCompany(true);
    try {
      const response = await companiesAPI.create({ name: companySearch.trim() });
      const newCompany = response.data.company;
      setCompanies(prev => [...prev, newCompany].sort((a, b) => a.name.localeCompare(b.name)));
      selectCompany(newCompany);
    } catch (error: any) {
      console.error('Failed to create company:', error);
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleReferrerSearch = (value: string) => {
    setReferrerSearch(value);
    setShowReferrerDropdown(true);

    // Clear selected referrer if user changes input
    if (formData.referredById) {
      setFormData(prev => ({ ...prev, referredById: '' }));
    }

    // Debounced API search
    if (referrerDebounceRef.current) {
      clearTimeout(referrerDebounceRef.current);
    }

    if (value.length < 2) {
      setReferrerResults([]);
      return;
    }

    referrerDebounceRef.current = setTimeout(async () => {
      try {
        const response = await contactsAPI.searchReferrers(value);
        // Exclude current contact from results (prevent self-referral)
        const filtered = contact?.id
          ? response.data.contacts.filter((c: any) => c.id !== contact.id)
          : response.data.contacts;
        setReferrerResults(filtered);
      } catch (error) {
        console.error('Failed to search referrers:', error);
      }
    }, 300);
  };

  const selectReferrer = (referrer: { id: string; firstName: string; lastName: string }) => {
    setFormData(prev => ({ ...prev, referredById: referrer.id }));
    setReferrerSearch(`${referrer.firstName} ${referrer.lastName}`);
    setShowReferrerDropdown(false);
  };

  const clearReferrer = () => {
    setFormData(prev => ({ ...prev, referredById: '' }));
    setReferrerSearch('');
    setReferrerResults([]);
  };

  const handleQuickAddReferrer = async () => {
    if (!referrerQuickAddData.firstName.trim() || !referrerQuickAddData.lastName.trim()) return;
    setCreatingReferrer(true);
    try {
      const response = await contactsAPI.create({
        firstName: referrerQuickAddData.firstName.trim(),
        lastName: referrerQuickAddData.lastName.trim(),
        email: referrerQuickAddData.email.trim() || undefined,
        phone: referrerQuickAddData.phone.trim() || undefined,
        tags: []
      });
      const newContact = response.data.contact;
      selectReferrer(newContact);
      setShowReferrerQuickAdd(false);
      setReferrerQuickAddData({ firstName: '', lastName: '', email: '', phone: '' });
    } catch (error: any) {
      console.error('Failed to create referrer:', error);
      alert(error.response?.data?.error || 'Failed to create contact');
    } finally {
      setCreatingReferrer(false);
    }
  };

  const openReferrerQuickAdd = () => {
    // Pre-fill name from search text
    const parts = referrerSearch.trim().split(/\s+/);
    setReferrerQuickAddData({
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' ') || '',
      email: '',
      phone: ''
    });
    setShowReferrerQuickAdd(true);
    setShowReferrerDropdown(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name.startsWith('custom_')) {
      const fieldName = name.replace('custom_', '');
      const field = customFields.find(f => f.name === fieldName);
      
      let processedValue: any = value;
      if (field?.type === 'checkbox') {
        processedValue = (e.target as HTMLInputElement).checked;
      } else if (field?.type === 'number') {
        processedValue = value ? Number(value) : '';
      }
      
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [fieldName]: processedValue,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    // More comprehensive email validation that allows + and other valid characters
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    // Validate custom fields
    customFields.forEach(field => {
      const value = formData.customFields[field.name];
      if (field.required && !value) {
        newErrors[`custom_${field.name}`] = `${field.label} is required`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      // Sync company name from selected company
      const selectedCompany = formData.companyId
        ? companies.find(c => c.id === formData.companyId)
        : null;

      // Convert empty strings to null for mortgage numeric/date fields
      const emptyToNull = (v: any) => (v === '' || v === undefined ? null : v);

      const data = {
        ...formData,
        company: selectedCompany ? selectedCompany.name : formData.company,
        tags: formData.tags,
        lastContacted: formData.lastContacted || null,
        companyId: formData.companyId || null,
        referredById: formData.referredById || null,
        // Address fields
        address1: emptyToNull(formData.address1),
        address2: emptyToNull(formData.address2),
        city: emptyToNull(formData.city),
        addressState: emptyToNull(formData.addressState),
        zip: emptyToNull(formData.zip),
        // Mortgage fields — convert empty to null
        dateOfBirth: emptyToNull(formData.dateOfBirth),
        maritalStatus: emptyToNull(formData.maritalStatus),
        dependentsCount: emptyToNull(formData.dependentsCount),
        citizenshipStatus: emptyToNull(formData.citizenshipStatus),
        employerName: emptyToNull(formData.employerName),
        employerPhone: emptyToNull(formData.employerPhone),
        yearsInProfession: emptyToNull(formData.yearsInProfession),
        monthlyIncome: emptyToNull(formData.monthlyIncome),
        incomeType: emptyToNull(formData.incomeType),
        loanPurpose: emptyToNull(formData.loanPurpose),
        propertyType: emptyToNull(formData.propertyType),
        propertyUse: emptyToNull(formData.propertyUse),
        estimatedPurchasePrice: emptyToNull(formData.estimatedPurchasePrice),
        desiredLoanAmount: emptyToNull(formData.desiredLoanAmount),
        estimatedCreditScore: emptyToNull(formData.estimatedCreditScore),
        downPaymentPercent: emptyToNull(formData.downPaymentPercent),
        preQualStatus: emptyToNull(formData.preQualStatus),
        preQualAmount: emptyToNull(formData.preQualAmount),
        preQualExpiration: emptyToNull(formData.preQualExpiration),
        // Real Estate — Client Profile
        clientType: emptyToNull(formData.clientType),
        preApprovalStatus: emptyToNull(formData.preApprovalStatus),
        preApprovalAmount: emptyToNull(formData.preApprovalAmount),
        priceRangeMin: emptyToNull(formData.priceRangeMin),
        priceRangeMax: emptyToNull(formData.priceRangeMax),
        desiredArea: emptyToNull(formData.desiredArea),
        preferredPropertyType: emptyToNull(formData.preferredPropertyType),
        bedroomsNeeded: emptyToNull(formData.bedroomsNeeded),
        bathroomsNeeded: emptyToNull(formData.bathroomsNeeded),
        moveInTimeline: emptyToNull(formData.moveInTimeline),
        currentHousingSituation: emptyToNull(formData.currentHousingSituation),
        // Real Estate — Seller Profile
        rePropertyAddress: emptyToNull(formData.rePropertyAddress),
        estimatedHomeValue: emptyToNull(formData.estimatedHomeValue),
        mortgageBalance: emptyToNull(formData.mortgageBalance),
        reasonForSelling: emptyToNull(formData.reasonForSelling),
        desiredTimeline: emptyToNull(formData.desiredTimeline),
      };


      let result;
      if (contact) {
        const response = await contactsAPI.update(contact.id, data);
        result = response.data.contact;
      } else {
        const response = await contactsAPI.create(data);
        result = response.data.contact;
      }

      onSubmit(result);
    } catch (error: any) {
      console.error('Failed to save contact:', error);
      
      let errorMessage = 'Failed to save contact';
      
      if (error.response?.data?.errors) {
        // Express validator errors
        errorMessage = error.response.data.errors.map((err: any) => err.msg).join(', ');
      } else if (error.response?.data?.error) {
        // Custom error message
        errorMessage = error.response.data.error;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCustomField = (field: CustomField) => {
    const value = formData.customFields[field.name] || '';
    const error = errors[`custom_${field.name}`];
    
    switch (field.type) {
      case 'textarea':
        return (
          <div key={field.id} className="sm:col-span-2">
            <label htmlFor={`custom_${field.name}`} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <textarea
              id={`custom_${field.name}`}
              name={`custom_${field.name}`}
              rows={3}
              value={value}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border ${
                error ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm text-primary-dark placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-mobile-sm touch-target`}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      case 'select':
        return (
          <div key={field.id}>
            <label htmlFor={`custom_${field.name}`} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <select
              id={`custom_${field.name}`}
              name={`custom_${field.name}`}
              value={value}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border ${
                error ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm text-primary-dark placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-mobile-sm touch-target`}
            >
              <option value="">Select...</option>
              {field.options?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      case 'checkbox':
        return (
          <div key={field.id} className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id={`custom_${field.name}`}
                name={`custom_${field.name}`}
                type="checkbox"
                checked={value || false}
                onChange={handleChange}
                className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor={`custom_${field.name}`} className="font-medium text-gray-700">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
        
      default:
        return (
          <div key={field.id}>
            <label htmlFor={`custom_${field.name}`} className="block text-sm font-medium text-gray-700">
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </label>
            <input
              id={`custom_${field.name}`}
              name={`custom_${field.name}`}
              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'url' ? 'url' : 'text'}
              value={value}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border ${
                error ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm text-primary-dark placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-mobile-sm touch-target`}
            />
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>
        );
    }
  };

  return (
    <div className="bg-white">
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold leading-6 text-primary-dark">
            {contact ? 'Edit Contact' : 'New Contact'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 px-6 py-5 rounded-lg">
            <h4 className="text-sm font-medium text-primary-dark mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <FormField
                label="First Name"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={errors.firstName}
                required
              />

              <FormField
                label="Last Name"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={errors.lastName}
                required
              />

              <FormField
                label="Email"
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
              />

              <FormField
                label="Phone"
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={() => setFormData(prev => ({ ...prev, phone: normalizePhone(prev.phone) }))}
              />

              <FormField
                label="Address Line 1"
                id="address1"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
              />

              <FormField
                label="Address Line 2"
                id="address2"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
              />

              <FormField
                label="City"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />

              <FormField
                label="State"
                id="addressState"
                name="addressState"
                value={formData.addressState}
                onChange={handleChange}
              />

              <FormField
                label="Zip"
                id="zip"
                name="zip"
                value={formData.zip}
                onChange={handleChange}
              />

              {/* Referred By - searchable selector with quick-add */}
              <div ref={referrerRef} className="relative">
                <label htmlFor="referrerSearch" className="block text-sm font-medium text-gray-700">
                  Referred By
                </label>
                <div className="mt-1 relative">
                  <input
                    type="text"
                    id="referrerSearch"
                    value={referrerSearch}
                    onChange={(e) => handleReferrerSearch(e.target.value)}
                    onFocus={() => { if (referrerSearch.length >= 2) setShowReferrerDropdown(true); }}
                    placeholder="Search for a referrer..."
                    className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm text-primary-dark placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-mobile-sm touch-target"
                  />
                  {formData.referredById && (
                    <button
                      type="button"
                      onClick={clearReferrer}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {showReferrerDropdown && referrerSearch.length >= 2 && (
                  <div className="absolute z-20 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-48 overflow-auto">
                    {referrerResults.length > 0 ? (
                      referrerResults.map((ref) => (
                        <button
                          key={ref.id}
                          type="button"
                          onClick={() => selectReferrer(ref)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            formData.referredById === ref.id ? 'bg-indigo-50 text-primary font-medium' : 'text-gray-700'
                          }`}
                        >
                          <div>{ref.firstName} {ref.lastName}</div>
                          {(ref.email || ref.phone) && (
                            <div className="text-xs text-gray-400">{ref.email || ref.phone}</div>
                          )}
                        </button>
                      ))
                    ) : null}

                    {/* Quick-add option */}
                    <button
                      type="button"
                      onClick={openReferrerQuickAdd}
                      className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-indigo-50 font-medium border-t border-gray-100 flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create "{referrerSearch.trim()}"
                    </button>
                  </div>
                )}
              </div>

              <FormField
                label="Last Contacted"
                id="lastContacted"
                name="lastContacted"
                type="date"
                value={formData.lastContacted}
                onChange={handleChange}
              />
            </div>
          </div>
          
          {/* Additional Information */}
          <div className="bg-gray-50 px-6 py-5 rounded-lg">
            <h4 className="text-sm font-medium text-primary-dark mb-4">Additional Information</h4>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                {/* Company - searchable selector with inline create */}
                <div ref={companyRef} className="relative">
                  <label htmlFor="companySearch" className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <div className="mt-1 relative">
                    <input
                      type="text"
                      id="companySearch"
                      value={companySearch}
                      onChange={(e) => {
                        setCompanySearch(e.target.value);
                        setShowCompanyDropdown(true);
                        // Clear the selected companyId if user is typing something different
                        if (formData.companyId) {
                          const selected = companies.find(c => c.id === formData.companyId);
                          if (selected && selected.name !== e.target.value) {
                            setFormData(prev => ({ ...prev, companyId: '', company: '' }));
                          }
                        }
                      }}
                      onFocus={() => setShowCompanyDropdown(true)}
                      placeholder="Search or create a company..."
                      className="block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm text-primary-dark placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-mobile-sm touch-target"
                    />
                    {formData.companyId && (
                      <button
                        type="button"
                        onClick={clearCompany}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {showCompanyDropdown && companySearch && (
                    <div className="absolute z-20 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-48 overflow-auto">
                      {filteredCompanies.length > 0 ? (
                        filteredCompanies.slice(0, 10).map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => selectCompany(company)}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                              formData.companyId === company.id ? 'bg-indigo-50 text-primary font-medium' : 'text-gray-700'
                            }`}
                          >
                            {company.name}
                          </button>
                        ))
                      ) : null}

                      {/* Create new option - show when no exact match */}
                      {!companies.some(c => c.name.toLowerCase() === companySearch.toLowerCase().trim()) && (
                        <button
                          type="button"
                          onClick={handleCreateCompany}
                          disabled={creatingCompany}
                          className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-indigo-50 font-medium border-t border-gray-100 flex items-center"
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          {creatingCompany ? 'Creating...' : `Create "${companySearch.trim()}"`}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <FormField
                  label="Position"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                />
              </div>

              <TagInput
                value={formData.tags}
                onChange={(tags) => setFormData({ ...formData, tags })}
                placeholder="customer, vip, lead"
                error={errors.tags}
              />
              
              {/* contacts.notes TEXT column — standing profile context.
                  Distinct from the dated note records shown in the Interaction Log (NoteWidget). */}
              <div>
                <FormTextarea
                  label="Profile Notes"
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Standing background about this person that stays on the record. For dated call, text,
                  and meeting notes, use the Interaction Log.
                </p>
              </div>
            </div>
          </div>
          
          {/* Mortgage 1003 — Borrower Profile */}
          {showBorrowerProfile && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Borrower Profile</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <FormField
                  label="Date of Birth"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Marital Status"
                  id="maritalStatus"
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {MARITAL_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Number of Dependents"
                  id="dependentsCount"
                  name="dependentsCount"
                  type="number"
                  value={formData.dependentsCount}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Citizenship Status"
                  id="citizenshipStatus"
                  name="citizenshipStatus"
                  value={formData.citizenshipStatus}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {CITIZENSHIP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
              </div>
            </div>
          )}

          {/* Mortgage 1003 — Employment & Income */}
          {showEmploymentIncome && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Employment & Income</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <FormField
                  label="Employer Name"
                  id="employerName"
                  name="employerName"
                  value={formData.employerName}
                  onChange={handleChange}
                />
                <FormField
                  label="Employer Phone"
                  id="employerPhone"
                  name="employerPhone"
                  type="tel"
                  value={formData.employerPhone}
                  onChange={handleChange}
                  onBlur={() => setFormData(prev => ({ ...prev, employerPhone: normalizePhone(prev.employerPhone) }))}
                />
                <FormField
                  label="Years in Profession"
                  id="yearsInProfession"
                  name="yearsInProfession"
                  type="number"
                  value={formData.yearsInProfession}
                  onChange={handleChange}
                />
                <FormField
                  label="Monthly Income"
                  id="monthlyIncome"
                  name="monthlyIncome"
                  type="number"
                  value={formData.monthlyIncome}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Income Type"
                  id="incomeType"
                  name="incomeType"
                  value={formData.incomeType}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {INCOME_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <div className="flex items-center pt-6">
                  <input
                    id="selfEmployed"
                    name="selfEmployed"
                    type="checkbox"
                    checked={formData.selfEmployed as boolean}
                    onChange={(e) => setFormData(prev => ({ ...prev, selfEmployed: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="selfEmployed" className="ml-2 text-sm font-medium text-gray-700">
                    Self-Employed
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Mortgage 1003 — Loan Preferences */}
          {showLoanPreferences && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Loan Preferences</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <FormSelect
                  label="Loan Purpose"
                  id="loanPurpose"
                  name="loanPurpose"
                  value={formData.loanPurpose}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {LOAN_PURPOSE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormSelect
                  label="Property Type"
                  id="propertyType"
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {PROPERTY_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormSelect
                  label="Property Use"
                  id="propertyUse"
                  name="propertyUse"
                  value={formData.propertyUse}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {PROPERTY_USE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Estimated Purchase Price"
                  id="estimatedPurchasePrice"
                  name="estimatedPurchasePrice"
                  type="number"
                  value={formData.estimatedPurchasePrice}
                  onChange={handleChange}
                />
                <FormField
                  label="Desired Loan Amount"
                  id="desiredLoanAmount"
                  name="desiredLoanAmount"
                  type="number"
                  value={formData.desiredLoanAmount}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Estimated Credit Score"
                  id="estimatedCreditScore"
                  name="estimatedCreditScore"
                  value={formData.estimatedCreditScore}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {CREDIT_SCORE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Down Payment %"
                  id="downPaymentPercent"
                  name="downPaymentPercent"
                  type="number"
                  value={formData.downPaymentPercent}
                  onChange={handleChange}
                />
                <div className="flex items-center pt-6">
                  <input
                    id="firstTimeHomeBuyer"
                    name="firstTimeHomeBuyer"
                    type="checkbox"
                    checked={formData.firstTimeHomeBuyer as boolean}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstTimeHomeBuyer: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="firstTimeHomeBuyer" className="ml-2 text-sm font-medium text-gray-700">
                    First-Time Home Buyer
                  </label>
                </div>
                <div className="flex items-center pt-2">
                  <input
                    id="isVeteran"
                    name="isVeteran"
                    type="checkbox"
                    checked={formData.isVeteran as boolean}
                    onChange={(e) => setFormData(prev => ({ ...prev, isVeteran: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="isVeteran" className="ml-2 text-sm font-medium text-gray-700">
                    Veteran (VA Eligible)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Mortgage 1003 — Pre-Qualification */}
          {showPreQualification && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Pre-Qualification</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <FormSelect
                  label="Pre-Qualification Status"
                  id="preQualStatus"
                  name="preQualStatus"
                  value={formData.preQualStatus}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {PRE_QUAL_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Pre-Qualification Amount"
                  id="preQualAmount"
                  name="preQualAmount"
                  type="number"
                  value={formData.preQualAmount}
                  onChange={handleChange}
                />
                <FormField
                  label="Pre-Qualification Expiration"
                  id="preQualExpiration"
                  name="preQualExpiration"
                  type="date"
                  value={formData.preQualExpiration}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* Real Estate — Client Profile */}
          {showClientProfile && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Client Profile</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <FormSelect
                  label="Client Type"
                  id="clientType"
                  name="clientType"
                  value={formData.clientType}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {CLIENT_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormSelect
                  label="Pre-Approval Status"
                  id="preApprovalStatus"
                  name="preApprovalStatus"
                  value={formData.preApprovalStatus}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {PRE_APPROVAL_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Pre-Approval Amount"
                  id="preApprovalAmount"
                  name="preApprovalAmount"
                  type="number"
                  value={formData.preApprovalAmount}
                  onChange={handleChange}
                />
                <FormField
                  label="Price Range Min"
                  id="priceRangeMin"
                  name="priceRangeMin"
                  type="number"
                  value={formData.priceRangeMin}
                  onChange={handleChange}
                />
                <FormField
                  label="Price Range Max"
                  id="priceRangeMax"
                  name="priceRangeMax"
                  type="number"
                  value={formData.priceRangeMax}
                  onChange={handleChange}
                />
                <FormField
                  label="Desired Area"
                  id="desiredArea"
                  name="desiredArea"
                  value={formData.desiredArea}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Preferred Property Type"
                  id="preferredPropertyType"
                  name="preferredPropertyType"
                  value={formData.preferredPropertyType}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {PREFERRED_PROPERTY_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormField
                  label="Bedrooms Needed"
                  id="bedroomsNeeded"
                  name="bedroomsNeeded"
                  type="number"
                  value={formData.bedroomsNeeded}
                  onChange={handleChange}
                />
                <FormField
                  label="Bathrooms Needed"
                  id="bathroomsNeeded"
                  name="bathroomsNeeded"
                  type="number"
                  value={formData.bathroomsNeeded}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Move-In Timeline"
                  id="moveInTimeline"
                  name="moveInTimeline"
                  value={formData.moveInTimeline}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {MOVE_IN_TIMELINE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormSelect
                  label="Current Housing Situation"
                  id="currentHousingSituation"
                  name="currentHousingSituation"
                  value={formData.currentHousingSituation}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {CURRENT_HOUSING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <div className="flex items-center pt-6">
                  <input
                    id="hasAgentAgreement"
                    name="hasAgentAgreement"
                    type="checkbox"
                    checked={formData.hasAgentAgreement as boolean}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasAgentAgreement: e.target.checked }))}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="hasAgentAgreement" className="ml-2 text-sm font-medium text-gray-700">
                    Has Agent Agreement
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Real Estate — Seller Profile */}
          {showSellerProfile && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Seller Profile</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <FormField
                    label="Property Address"
                    id="rePropertyAddress"
                    name="rePropertyAddress"
                    value={formData.rePropertyAddress}
                    onChange={handleChange}
                  />
                </div>
                <FormField
                  label="Estimated Home Value"
                  id="estimatedHomeValue"
                  name="estimatedHomeValue"
                  type="number"
                  value={formData.estimatedHomeValue}
                  onChange={handleChange}
                />
                <FormField
                  label="Mortgage Balance"
                  id="mortgageBalance"
                  name="mortgageBalance"
                  type="number"
                  value={formData.mortgageBalance}
                  onChange={handleChange}
                />
                <FormSelect
                  label="Reason for Selling"
                  id="reasonForSelling"
                  name="reasonForSelling"
                  value={formData.reasonForSelling}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {REASON_FOR_SELLING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
                <FormSelect
                  label="Desired Timeline"
                  id="desiredTimeline"
                  name="desiredTimeline"
                  value={formData.desiredTimeline}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {DESIRED_TIMELINE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </FormSelect>
              </div>
            </div>
          )}

          {customFields.length > 0 && (
            <div className="bg-gray-50 px-6 py-5 rounded-lg">
              <h4 className="text-sm font-medium text-primary-dark mb-4">Custom Fields</h4>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                {customFields.map(renderCustomField)}
              </div>
            </div>
          )}
          
          <div className="pt-6 border-t border-gray-200">
            <div className="flex items-center justify-end gap-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-primary-dark shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : contact ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>

        {/* Quick-Add Referrer Modal */}
        {showReferrerQuickAdd && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-primary-dark">Quick Add Referrer</h4>
                <button
                  type="button"
                  onClick={() => setShowReferrerQuickAdd(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">First Name *</label>
                    <input
                      type="text"
                      value={referrerQuickAddData.firstName}
                      onChange={(e) => setReferrerQuickAddData(prev => ({ ...prev, firstName: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                    <input
                      type="text"
                      value={referrerQuickAddData.lastName}
                      onChange={(e) => setReferrerQuickAddData(prev => ({ ...prev, lastName: e.target.value }))}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={referrerQuickAddData.email}
                    onChange={(e) => setReferrerQuickAddData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone</label>
                  <input
                    type="tel"
                    value={referrerQuickAddData.phone}
                    onChange={(e) => setReferrerQuickAddData(prev => ({ ...prev, phone: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowReferrerQuickAdd(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleQuickAddReferrer}
                    disabled={creatingReferrer || !referrerQuickAddData.firstName.trim() || !referrerQuickAddData.lastName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creatingReferrer ? 'Creating...' : 'Create & Select'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactForm;